import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { rememberSquab, saveSquabCard, squabCard, squabUrl, squabShareText } from "@/lib/squab";
import { ShareActions } from "@/components/ShareActions";
import { SquabCard } from "@/components/SquabCard";
import { prepareFacebookCard } from "@/lib/publicSharing";

export default function Squab() {
  const { challengeId } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [battle, setBattle] = useState(null);
  const [publicLink, setPublicLink] = useState("");
  useEffect(() => setPublicLink(""), [challengeId]);
  // Polls may return the same state in a fresh object; keep the prepared image
  // and metadata URL ready for the next real user gesture.
  const cardState = JSON.stringify(battle);
  const createCard = useCallback(() => squabCard(JSON.parse(cardState)), [cardState]);
  const facebookCard = useCallback(blob => prepareFacebookCard(blob, { challenge_id: challengeId }), [challengeId]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const sharing = useRef(null);
  const signIn = () => { rememberSquab(`/squab/${challengeId}`); navigate("/login"); };
  useEffect(() => {
    if (!user) return;
    let cancelled = false, timer;
    setBattle(null); setError("");
    const refresh = async () => {
      try {
        const { data } = await api.get(`/squab/${challengeId}`);
        if (cancelled) return;
        setBattle(data); setError("");
        if (!data.finished) timer = setTimeout(refresh, 5000);
      } catch (e) { if (!cancelled) setError(apiError(e)); }
    };
    refresh();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [challengeId, user]);
  const play = async () => {
    setBusy(true);
    try {
      // Claim the opponent slot before navigating. The subsequent start resumes it.
      await api.post(`/squab/${challengeId}/start`);
      navigate(`/play/casual?squab=${encodeURIComponent(challengeId)}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const rematch = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/squab/${challengeId}/rematch`);
      navigate(`/squab/${data.challenge_id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Layout>
      <div className="hhkd-squab">
        <Link to="/" className="hhkd-squab-back">← HHKD</Link>
        {loading ? <p>Loading…</p> : !user ? (
          <SquabCard onPlay={signIn} />
        ) : error ? <p role="alert">{error}</p> : !battle ? <p>Loading Squab…</p> : (
          <>
            <SquabCard battle={battle} busy={busy} onPlay={play} onRematch={rematch} onChallenge={() => sharing.current?.querySelector('[data-testid="share-native"]')?.click()} />
            {battle.is_participant && <section ref={sharing} className="squab-sharing" aria-label="Share this Squab">
              <ShareActions text={squabShareText(battle)} url={squabUrl(battle.challenge_id)} createCard={createCard} facebookCard={facebookCard} nativeShare preparePublicLink={!battle.finished} onPublicLink={setPublicLink} filename="HHKD-Squab.png" copyLabel={battle.finished ? "COPY BATTLE REPORT" : "COPY CHALLENGE LINK"} />
              <button type="button" onClick={async () => { try { await saveSquabCard(battle); } catch (e) { toast.error(e.message); } }} className="squab-save-card">Save Card</button>
              <label className="squab-link-label">Challenge link<input readOnly aria-label="Challenge link" value={publicLink || squabUrl(battle.challenge_id)} onFocus={e => e.target.select()} /></label>
            </section>}
          </>
        )}
      </div>
    </Layout>
  );
}
